import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import {
  clearGoogleMapsInstanceListeners,
  isGoogleMapsMissingApiKeyError,
  loadGoogleMapsScript,
} from '~/app/lib/google-maps';
import {
  FOCUSED_SITE_MAP_STYLES,
  createGoogleMapsPinMarkerIcon,
} from '~/app/lib/google-maps-presets';
import type { SiteAssetsMapProps } from '~/app/components/site-asset-map/SiteAssetsMap.types';
import {
  getMarkerColor,
  getMarkerCoordinates,
  getPolygonCenter,
} from '~/app/components/site-asset-map/siteAssetsMap.utils';

const { Text } = Typography;

export function SiteAssetsMap({
  assets,
  latitude,
  longitude,
  perimeter,
  overlayPerimeter,
  siteName,
  mode,
  emptyText,
  unavailableText,
  missingKeyText,
}: SiteAssetsMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<object | null>(null);
  const markerRefs = useRef<object[]>([]);
  const polygonRefs = useRef<object[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const coordinates = useMemo(() => {
    if (latitude !== null && longitude !== null) {
      return { lat: latitude, lng: longitude };
    }

    if (perimeter?.length) {
      return getPolygonCenter(perimeter);
    }

    if (overlayPerimeter?.length) {
      return getPolygonCenter(overlayPerimeter);
    }

    return null;
  }, [latitude, longitude, overlayPerimeter, perimeter]);

  const markers = useMemo(() => {
    if (!coordinates) {
      return [];
    }

    return assets.map((asset, index) => ({
      title: `${asset.assetName} · ${asset.openIncidentsCount} open incidents`,
      position: getMarkerCoordinates(
        coordinates.lat,
        coordinates.lng,
        index,
        assets.length,
      ),
      icon: createGoogleMapsPinMarkerIcon(
        getMarkerColor(asset.openIncidentsCount),
      ),
    }));
  }, [assets, coordinates]);

  useEffect(() => {
    if (!coordinates || !mapContainerRef.current) {
      return;
    }

    let isCancelled = false;

    void loadGoogleMapsScript()
      .then((googleMaps) => {
        if (isCancelled || !mapContainerRef.current) {
          return;
        }

        setErrorMessage(null);

        markerRefs.current.forEach((marker) => {
          clearGoogleMapsInstanceListeners(marker);
        });
        markerRefs.current = [];

        polygonRefs.current.forEach((polygon) => {
          clearGoogleMapsInstanceListeners(polygon);
        });
        polygonRefs.current = [];

        mapRef.current = new googleMaps.maps.Map(mapContainerRef.current, {
          center: coordinates,
          zoom:
            mode === 'focused'
              ? markers.length > 1
                ? 17
                : 16
              : markers.length > 1
                ? 16
                : 15,
          disableDefaultUI: false,
          fullscreenControl: true,
          mapTypeControl: mode === 'standard',
          streetViewControl: false,
          zoomControl: true,
          styles: mode === 'focused' ? FOCUSED_SITE_MAP_STYLES : undefined,
        });

        if (overlayPerimeter && overlayPerimeter.length >= 3) {
          polygonRefs.current.push(
            new googleMaps.maps.Polygon({
              map: mapRef.current as object,
              paths: overlayPerimeter,
              editable: false,
              draggable: false,
              clickable: false,
              strokeColor: '#38bdf8',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#7dd3fc',
              fillOpacity: 0.16,
            }),
          );
        }

        if (perimeter && perimeter.length >= 3) {
          polygonRefs.current.push(
            new googleMaps.maps.Polygon({
              map: mapRef.current as object,
              paths: perimeter,
              editable: false,
              draggable: false,
              clickable: false,
              strokeColor: '#2563eb',
              strokeOpacity: 0.92,
              strokeWeight: 3,
              fillColor: '#60a5fa',
              fillOpacity: 0.22,
            }),
          );
        }

        markerRefs.current = markers.map(
          (marker) =>
            new googleMaps.maps.Marker({
              map: mapRef.current as object,
              position: marker.position,
              title: marker.title,
              icon: marker.icon,
            }),
        );
      })
      .catch((error: Error) => {
        if (isCancelled) {
          return;
        }

        setErrorMessage(
          isGoogleMapsMissingApiKeyError(error)
            ? missingKeyText
            : error.message,
        );
      });

    return () => {
      isCancelled = true;
      markerRefs.current.forEach((marker) => {
        clearGoogleMapsInstanceListeners(marker);
      });
      polygonRefs.current.forEach((polygon) => {
        clearGoogleMapsInstanceListeners(polygon);
      });
      clearGoogleMapsInstanceListeners(mapRef.current);
      markerRefs.current = [];
      polygonRefs.current = [];
    };
  }, [coordinates, markers, missingKeyText, mode, overlayPerimeter, perimeter]);

  if (!coordinates) {
    return (
      <div
        style={{
          minHeight: 360,
          borderRadius: 12,
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          textAlign: 'center',
        }}
      >
        <Text type="secondary">{unavailableText}</Text>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        style={{
          minHeight: 360,
          borderRadius: 12,
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          textAlign: 'center',
        }}
      >
        <Text type="secondary">{errorMessage}</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 360,
      }}
    >
      <div
        aria-label={`${siteName} asset map`}
        ref={mapContainerRef}
        style={{
          width: '100%',
          minHeight: 360,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#f8fafc',
        }}
      />

      {!assets.length ? (
        <div
          style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            maxWidth: 'calc(100% - 24px)',
            padding: '8px 12px',
            borderRadius: 10,
            background: 'rgba(255, 255, 255, 0.92)',
            border: '1px solid #d9e4f0',
          }}
        >
          <Text type="secondary">{emptyText}</Text>
        </div>
      ) : null}
    </div>
  );
}
